import { RefreshCw, Loader2, Layers } from "lucide-react";
import { formatDateTime, fmtNum } from "@/lib/testFormat";

const FEATURE_ROWS = [
  ["durationSec", "Test duration", "s"],
  ["jerkRms", "Jerk (RMS)", "g/s"],
  ["peakAccelG", "Peak acceleration", "g"],
  ["harshCount", "Harsh events", ""],
  ["rollRms", "Roll wobble (gyroX RMS)", "°/s"],
  ["pitchRms", "Pitch wobble (gyroY RMS)", "°/s"],
  ["vibrationG", "Vibration", "g"],
  ["turnCount", "Turn count", ""],
];

export function ReferenceProfilePanel({ profile, loading, error, referenceCount, onRefresh, onRebuild, rebuilding, message, onClear, clearing }) {
  return (
    <section data-testid="reference-profile-panel" className="bg-zinc-900 border border-white/10 rounded-xl p-5 mb-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
            <Layers className="w-4 h-4 text-sky-400" /> Reference Profile
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Built from every test flagged <span className="font-mono text-zinc-300">storeAsReference: true</span>. Candidates are scored against these limits. {referenceCount} test(s) currently flagged; at least 3 needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            title="Reload profile"
            className="p-2 text-zinc-400 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {profile && (
            <button
              type="button"
              data-testid="clear-reference-btn"
              onClick={onClear}
              disabled={clearing || rebuilding}
              className="inline-flex items-center gap-2 bg-zinc-800 text-zinc-300 border border-white/10 text-xs font-bold py-2 px-3 rounded-lg hover:bg-zinc-700 hover:text-white disabled:opacity-50"
            >
              {clearing && <Loader2 className="w-3 h-3 animate-spin" />}
              Clear profile
            </button>
          )}
          <button
            type="button"
            data-testid="rebuild-reference-btn"
            onClick={onRebuild}
            disabled={rebuilding || referenceCount < 3}
            className="inline-flex items-center gap-2 bg-sky-500 text-black text-xs font-bold py-2 px-3 rounded-lg hover:bg-sky-400 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed"
          >
            {rebuilding && <Loader2 className="w-3 h-3 animate-spin" />}
            Rebuild from {referenceCount} reference test(s)
          </button>
        </div>
      </div>

      {message && (
        <p role="status" data-testid="reference-message" className={`text-xs mb-3 ${message.type === "success" ? "text-emerald-400" : "text-red-400"}`}>
          {message.text}
        </p>
      )}

      {loading ? (
        <p role="status" className="text-sm text-zinc-400">Loading reference profile...</p>
      ) : error ? (
        <p role="alert" className="text-sm text-red-400">Unable to load reference profile: {error}</p>
      ) : !profile ? (
        <p data-testid="no-reference-profile" className="text-sm text-zinc-400">
          No reference profile yet. Candidates are scored with default limits until you flag 3+ reference tests and rebuild.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-4">
            <Summary label="Reference runs" value={profile.referenceRuns} />
            <Summary label="Expected sequence" value={(profile.expectedSequence || []).map((d) => d[0]).join("-")} />
            <Summary label="Sequence source" value={profile.sequenceLearned ? "Learned from refs" : "Default R-L-R-L"} />
            <Summary label="Built" value={formatDateTime(profile.createdAt)} />
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Tests: <span className="font-mono text-zinc-300">{(profile.testIds || []).join(", ")}</span>
          </p>
          <div className="overflow-x-auto border-t border-white/10 pt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase text-zinc-500 text-left">
                  <th className="py-1 pr-4 font-normal">Feature</th>
                  <th className="py-1 pr-4 font-normal">Mean</th>
                  <th className="py-1 pr-4 font-normal">Min</th>
                  <th className="py-1 pr-4 font-normal">Max</th>
                  <th className="py-1 font-normal">Limit</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map(([key, label, unit]) => {
                  const f = profile.features?.[key];
                  if (!f) return null;
                  return (
                    <tr key={key} className="border-t border-white/5">
                      <td className="py-1.5 pr-4 text-zinc-300">{label} {unit && <span className="text-zinc-600">({unit})</span>}</td>
                      <td className="py-1.5 pr-4 font-mono text-white">{fmtNum(f.mean, 3)}</td>
                      <td className="py-1.5 pr-4 font-mono text-zinc-400">{fmtNum(f.min, 3)}</td>
                      <td className="py-1.5 pr-4 font-mono text-zinc-400">{fmtNum(f.max, 3)}</td>
                      <td className="py-1.5 font-mono text-yellow-400">{fmtNum(f.limit, 3)}</td>
                    </tr>
                  );
                })}
                {["RIGHT", "LEFT"].map((dir) => {
                  const d = profile.turns?.byDirection?.[dir];
                  if (!d) return null;
                  return (
                    <tr key={dir} className="border-t border-white/5">
                      <td className="py-1.5 pr-4 text-zinc-300">{dir} turn: peak rate / duration</td>
                      <td className="py-1.5 pr-4 font-mono text-white">{fmtNum(d.avgPeakRate, 1)} / {d.avgDurationMs}ms</td>
                      <td className="py-1.5 pr-4 font-mono text-zinc-500">—</td>
                      <td className="py-1.5 pr-4 font-mono text-zinc-500">—</td>
                      <td className="py-1.5 font-mono text-yellow-400">{fmtNum(d.maxPeakRate, 1)} / {d.maxDurationMs}ms</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Summary({ label, value }) {
  return (
    <div>
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="font-mono text-white mt-1 text-sm">{value ?? "—"}</p>
    </div>
  );
}
